import React, { useState, useCallback, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Grid, TextField, Button, CircularProgress, Box, Chip, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSnackbar from "../Common/snackBar";
import { getEmailTemplateDetailsById, updateEmailTemplate } from "../../Actions/EmailTemplateAction";
import TipTapEditorComponent from "../../components/Common/TipTapEditorComponent";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

const EmailTemplateEditModal = ({ emailTemplateId, open, onOpenChange, fetchEmailTemplatesList }) => {
  const [loading, setLoading] = useState({
    fetching: true,
    submitting: false,
  });

  const [emailTemplate, setEmailTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const setLoadingState = useCallback((key, value) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string().trim()
      .required(VALIDATION.REQUIRED_FIELD("Title"))
      .min(1, VALIDATION.MIN_LENGTH("Title", 1))
      .max(100, VALIDATION.MAX_LENGTH("Title", 100))
      .matches(/^[a-zA-Z0-9\s_\-]+$/, VALIDATION.INVALID_FORMAT("Title")),
    subject: Yup.string()
        .trim()
        .required(VALIDATION.REQUIRED_FIELD("Subject"))
        .min(1, VALIDATION.MIN_LENGTH("Subject", 1))
        .matches(/^[a-zA-Z0-9\s_\-]+$/, VALIDATION.INVALID_FORMAT("Subject")),
    body: Yup.string().required(VALIDATION.REQUIRED_FIELD("Email body")),

  });

  const formik = useFormik({
    initialValues: {
      title: "",
      subject: "",
      body: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setLoadingState("submitting", true);
        
        const payload = {
          id: emailTemplateId,
          title: values.title.trim(),
          subject: values.subject.trim(),
          body: values.body.trim(),
        };


        const response = await updateEmailTemplate(payload);
        const { success, message } = response;
        if (success) {
          setSnackbar({
            open: true,
            message: message || "Email template updated successfully.",
            severity: "success",
          });
          fetchEmailTemplatesList?.();
          setTimeout(() => {
            onOpenChange(false);
            setEmailTemplate(null);
          }, 600);
        } else {
          setSnackbar({
            open: true,
            message: response.message || "Unable to update template.",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Update failed:", error);
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || error.message || "An error occurred while updating the template.",
          severity: "error",
        });
      } finally {
        setLoadingState("submitting", false);
      }
    },
  });

  const fetchEmailTemplateDetails = useCallback(async () => {
    if (!emailTemplateId) return;

    setLoadingState("fetching", true);

    try {
      const response = await getEmailTemplateDetailsById(emailTemplateId);

      if (response.success) {
        const data = response.data;
        setEmailTemplate(data);

        const { title, subject, body } = data;
        formik.setValues({ title, subject, body });
      } else {
        setSnackbar({
          open: true,
          message:
            response.message ||
            "An error occurred while fetching template details.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Failed to fetch emailTemplate details:", error);
      setSnackbar({
        open: true,
        message:
          "Unable to fetch email template details. Please try again.",
        severity: "error",
      });
    } finally {
      setLoadingState("fetching", false);
    }
  }, [emailTemplateId,setLoadingState]);


  useEffect(() => {
    if (open && emailTemplateId) { 
      fetchEmailTemplateDetails();
    }
  }, [open, emailTemplateId,fetchEmailTemplateDetails]);

  const handleModalClose = () => {
    onOpenChange(false);
    setEmailTemplate(null);
  };

  const handleFieldChange = (e) => {
    formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ''));
    formik.setFieldTouched(e.target.name, true, false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>
          <div className="d-flex align-items-center gap-3">
            <h2 className="m-0">Update Email Template</h2>

            {loading.fetching ? (
              <div
                style={{
                  width: "80px",
                  height: "28px",
                  borderRadius: "999px",
                  backgroundColor: "rgba(0,0,0,0.1)",
                  animation: "pulse 1.5s infinite",
                }}
              ></div>
            ) : (
              <Chip
                label={emailTemplate?.isTenantTemplate === false ? "Master" : "Tenant"}
                size="small"
                sx={{
                  fontWeight: 600,
                  borderRadius: "999px",
                  padding: "2px 6px",
                  textTransform: "capitalize",
                  backgroundColor:
                    emailTemplate?.isTenantTemplate === false
                      ? "rgba(25, 118, 210, 0.10)" // primary soft blue
                      : "rgba(156, 39, 176, 0.12)", // secondary soft purple
                  color: emailTemplate?.isTenantTemplate === false ? "primary.main" : "secondary.main",
                  border: "none",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              />
            )}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={3}
            >
              {/* Title */}
              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography variant="subtitle2" gutterBottom> Title <span className="text-danger ms-1">*</span></Typography>
                <TextField
                  fullWidth
                  id="title"
                  name="title"
                  placeholder="Enter title"
                  value={formik.values.title}
                  onChange={handleFieldChange}
                  onBlur={(e) => {
                    formik.setFieldValue(e.target.name, e.target.value.trim());
                    formik.handleBlur(e);
                  }}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  disabled={loading.fetching || loading.submitting}
                />
              </Grid>

              {/* Subject */}
              <Grid
                item
                xs={12}
                md={6}
              >
                <Typography variant="subtitle2" gutterBottom> Subject <span className="text-danger ms-1">*</span></Typography>
                <TextField
                  fullWidth
                  id="subject"
                  name="subject"
                  placeholder="Enter subject"
                  value={formik.values.subject}
                  onChange={handleFieldChange}
                  onBlur={(e) => {
                    formik.setFieldValue(e.target.name, e.target.value.trim());
                    formik.handleBlur(e);
                  }}
                  error={formik.touched.subject && Boolean(formik.errors.subject)}
                  helperText={formik.touched.subject && formik.errors.subject}
                  disabled={loading.fetching || loading.submitting}
                />
              </Grid>

              {/* Body */}
              <Grid
                item
                xs={12}
              >
              <Typography variant="subtitle2" gutterBottom> Body <span className="text-danger ms-1">*</span></Typography>
                <TipTapEditorComponent
                  key={emailTemplate?.id}
                  initialValue={formik.values.body}
                  disablePlugins={["media", "image"]}
                  tokens={emailTemplate?.mergeTagsList || []}
                  handleChange={(content) => {
                    formik.setFieldValue("body", content); 
                    formik.setFieldTouched("body", true, false); 
                  }}
                  handleBlur={(content) => {
                    const trimmedContent = content?.trim() || "";
                    formik.setFieldValue("body", trimmedContent);
                    formik.setFieldTouched("body", true);
                  }}
                  isDisabled={loading.fetching || loading.submitting}
                  error={formik.errors.body || null}
                />
              </Grid>

              {/* Footer Buttons */}
              <Grid
                item
                xs={12}
              >
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                >
                  <Button
                    variant="outlined"
                    onClick={handleModalClose}
                    disabled={loading.submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading.fetching || loading.submitting || !formik.isValid}
                  >
                    {loading.submitting && (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ marginRight: 1 }}
                      />
                    )}
                    Save Changes
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

export default EmailTemplateEditModal;
